import { http, type RequestOptions } from './http';
import { ROUTES } from './config';
import type {
  CancelOrderInput,
  CohortAnalysis,
  CreateOrderInput,
  GlobalOrderHistoryPage,
  OrderAnalyticsSummary,
  OrderDetail,
  OrderFunnel,
  OrderHistoryPage,
  OrderListFilters,
  OrderListPage,
  OrderOverview,
  TopProduct,
  TrackedOrder,
  UpdateOrderStatusInput,
} from '../types/ecommerce';

/**
 * OrderService client. Requires a real login for the whole prefix (customer,manager — no
 * anonymous reads, unlike products/cart). Idempotency-Key is mandatory on create, same
 * cryptoKey() pattern bookingApi.createBooking uses.
 */
export const ordersApi = {
  create(body: CreateOrderInput, options: RequestOptions & { idempotencyKey?: string } = {}): Promise<OrderDetail> {
    const key = options.idempotencyKey ?? cryptoKey();
    return http.post(ROUTES.orders, body, { ...options, idempotencyKey: key });
  },

  /** A customer's own orders. Manager/admin get every order (staff-wide) unless
   * `customerId` scopes it to one customer — ignored server-side for a non-staff caller. */
  list(filters: OrderListFilters = {}): Promise<OrderListPage> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.cursor) params.set('cursor', filters.cursor);
    if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
    if (filters.customerId) params.set('customerId', filters.customerId);
    const qs = params.toString();
    return http.get(`${ROUTES.orders}${qs ? `?${qs}` : ''}`);
  },

  /** The owning customer, or manager/admin — the backend 403s a mismatch. */
  /** Anonymous tracking — the order number plus the e-mail the order was placed with. 404 on any mismatch. */
  track(orderNumber: string, email: string): Promise<TrackedOrder> {
    const q = new URLSearchParams({ orderNumber, email });
    return http.get(`${ROUTES.orders}/track?${q.toString()}`, { anonymous: true });
  },

  get(id: string): Promise<OrderDetail> {
    return http.get(`${ROUTES.orders}/${id}`);
  },

  /** The caller's own order count (self-scoped only — no customerId param), optionally
   * filtered by status. Real data for the storefront nav badge. */
  myCount(status?: string): Promise<{ count: number }> {
    return http.get(`${ROUTES.orders}/my-count${status ? `?status=${status}` : ''}`);
  },

  /** Admin/manager only. */
  updateStatus(id: string, body: UpdateOrderStatusInput): Promise<void> {
    return http.put(`${ROUTES.orders}/${id}/status`, body);
  },

  /** The owning customer only. */
  cancel(id: string, body: CancelOrderInput): Promise<void> {
    return http.put(`${ROUTES.orders}/${id}/cancel`, body);
  },

  /** Append-only audit trail, newest first, cursor-paginated. */
  getHistory(id: string, cursor?: string, pageSize = 20): Promise<OrderHistoryPage> {
    const params = new URLSearchParams({ pageSize: String(pageSize) });
    if (cursor) params.set('cursor', cursor);
    return http.get(`${ROUTES.orders}/${id}/history?${params.toString()}`);
  },

  /** Anonymous "Hot Sellers" widget — real units-sold from OrderItems, no login required
   * (gateway override of the parent /api/v1/orders auth gate — see appsettings.json). */
  topProducts(limit = 8, days = 30): Promise<TopProduct[]> {
    return http.get(`${ROUTES.orders}/analytics/top-products?limit=${limit}&days=${days}`, { anonymous: true });
  },

  /** Manager+ only — admin dashboard revenue/order overview. */
  analyticsSummary(days = 30): Promise<OrderAnalyticsSummary> {
    return http.get(`${ROUTES.orders}/analytics/summary?days=${days}`);
  },

  /** Manager+ only — order-lifecycle funnel (Placed → Processing → Completed / Cancelled). */
  analyticsFunnel(days = 30): Promise<OrderFunnel> {
    return http.get(`${ROUTES.orders}/analytics/funnel?days=${days}`);
  },

  /** Manager+ only — monthly retention by first-order cohort. */
  analyticsCohorts(months = 6): Promise<CohortAnalysis> {
    return http.get(`${ROUTES.orders}/analytics/cohorts?months=${months}`);
  },

  /** Manager+ only — dashboard KPI row (today/this-week orders+revenue, pending backlog,
   * active/total customers). */
  analyticsOverview(): Promise<OrderOverview> {
    return http.get(`${ROUTES.orders}/analytics/overview`);
  },

  /** Manager+ only — cross-order audit log (every order's status changes, not just one). */
  allHistory(filters: { status?: string; cursor?: string; pageSize?: number } = {}): Promise<GlobalOrderHistoryPage> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.cursor) params.set('cursor', filters.cursor);
    if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
    const qs = params.toString();
    return http.get(`${ROUTES.orders}/history${qs ? `?${qs}` : ''}`);
  },
};

function cryptoKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `order-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
